import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { collection, getDocs, doc, getDoc, setDoc, deleteDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { auth, db } from '../../services/firebase';
import { useAuthState } from 'react-firebase-hooks/auth';

function SuperAdminDashboard() {
  const navigate = useNavigate();
  const [user] = useAuthState(auth);
  const [profile, setProfile] = useState({ firstName: '', lastName: '', displayName: '' });
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [editingClub, setEditingClub] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [stats, setStats] = useState({
    totalClubs: 0,
    totalUsers: 0,
    totalPlayers: 0,
    totalTeams: 0
  });
  const [subscriptionAction, setSubscriptionAction] = useState({
    status: 'active',
    extendDays: 30
  });

  const [formData, setFormData] = useState({
    name: '',
    subdomain: '',
    email: '',
    phone: '',
    address: '',
    primaryColor: '#1e3a5f',
    secondaryColor: '#ce1126'
  });

  // Fetch user profile
  useEffect(() => {
    const fetchProfile = async () => {
      if (user) {
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            setProfile(userDoc.data());
          }
        } catch (error) {
          console.error('Error fetching profile:', error);
        }
      }
    };
    fetchProfile();
  }, [user]);

  // Fetch all clubs and stats, and fix missing homepage content
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch clubs
        const clubsSnapshot = await getDocs(collection(db, 'clubs'));
        const clubsList = clubsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setClubs(clubsList);

        // Fetch global stats
        const [usersSnapshot, playersSnapshot, teamsSnapshot] = await Promise.all([
          getDocs(collection(db, 'users')),
          getDocs(collection(db, 'players')),
          getDocs(collection(db, 'teams'))
        ]);

        setStats({
          totalClubs: clubsList.length,
          totalUsers: usersSnapshot.size,
          totalPlayers: playersSnapshot.size,
          totalTeams: teamsSnapshot.size
        });

        // Fix missing homepage content for all clubs
        for (const club of clubsList) {
          const contentId = `${club.id}_homepage`;
          const contentDoc = await getDoc(doc(db, 'content', contentId));
          if (!contentDoc.exists()) {
            console.log(`Creating missing homepage content for ${club.id}`);
            try {
              await setDoc(doc(db, 'content', contentId), {
                clubId: club.id,
                heroTitle: (club.name || club.id).toUpperCase(),
                heroSubtitle: 'Welcome to our club',
                statPlayers: '0',
                statAgeGroups: '0', 
                statCoaches: '0',
                statTournaments: '0',
                createdAt: new Date(),
                createdBy: 'super_admin_auto'
              });
              console.log(`Created homepage content for ${club.id}`);
            } catch (err) {
              console.error(`Failed to create homepage content for ${club.id}:`, err);
            }
          }
        }

      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const displayName = profile.firstName && profile.lastName 
    ? `${profile.firstName} ${profile.lastName}` 
    : profile.displayName || user?.email?.split('@')[0] || 'Super Admin';

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/admin');
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      subdomain: '',
      email: '',
      phone: '',
      address: '',
      primaryColor: '#1e3a5f',
      secondaryColor: '#ce1126'
    });
    setError('');
    setSuccess('');
  };

  const openAddModal = () => {
    resetForm();
    setShowAddModal(true);
  };

  const openEditModal = (club) => {
    setEditingClub(club);
    setFormData({
      name: club.name || '',
      subdomain: club.subdomain || '',
      email: club.email || '',
      phone: club.phone || '',
      address: club.address || '',
      primaryColor: club.primaryColor || '#1e3a5f',
      secondaryColor: club.secondaryColor || '#ce1126'
    });
    setError('');
    setSuccess('');
    setShowEditModal(true);
  };

  const generateSubdomain = (name) => {
    return name.toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '')
      .slice(0, 20);
  };

  const handleNameChange = (e) => {
    const name = e.target.value;
    setFormData({
      ...formData,
      name: name,
      subdomain: formData.subdomain || generateSubdomain(name)
    });
  };

  const handleAddClub = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);

    // Validate subdomain uniqueness
    const existingClub = clubs.find(c => c.subdomain === formData.subdomain);
    if (existingClub) {
      setError('This subdomain is already in use. Please choose a different one.');
      setSaving(false);
      return;
    }

    try {
      const clubId = formData.subdomain;
      const clubData = {
        ...formData,
        createdAt: new Date(),
        createdBy: user?.uid,
        status: 'active'
      };

      await setDoc(doc(db, 'clubs', clubId), clubData);
      
      setClubs([...clubs, { id: clubId, ...clubData }]);
      setStats({ ...stats, totalClubs: stats.totalClubs + 1 });
      setSuccess('Club created successfully!');
      
      setTimeout(() => {
        setShowAddModal(false);
        resetForm();
      }, 1500);

    } catch (error) {
      console.error('Error creating club:', error);
      setError('Failed to create club. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateClub = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);

    try {
      const clubData = {
        ...formData,
        updatedAt: new Date(),
        updatedBy: user?.uid
      };

      await setDoc(doc(db, 'clubs', editingClub.id), clubData, { merge: true });
      
      setClubs(clubs.map(c => 
        c.id === editingClub.id ? { ...c, ...clubData } : c
      ));
      setSuccess('Club updated successfully!');
      
      setTimeout(() => {
        setShowEditModal(false);
        setEditingClub(null);
        resetForm();
      }, 1500);

    } catch (error) {
      console.error('Error updating club:', error);
      setError('Failed to update club. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClub = async (clubId, clubName) => {
    if (!window.confirm(`Are you sure you want to delete "${clubName}"? This will NOT delete the club's data, but the club will no longer be accessible.`)) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'clubs', clubId));
      setClubs(clubs.filter(c => c.id !== clubId));
      setStats({ ...stats, totalClubs: stats.totalClubs - 1 });
    } catch (error) {
      console.error('Error deleting club:', error);
    }
  };

  const openSubscriptionModal = (club) => {
    setEditingClub(club);
    setSubscriptionAction({
      status: club.subscription?.status || 'active',
      extendDays: 30
    });
    setError('');
    setSuccess('');
    setShowSubscriptionModal(true);
  };

  const getSubscriptionStatusForClub = (club) => {
    if (!club.subscription) return 'unknown';
    const { status, currentPeriodEnd } = club.subscription;
    
    if (status === 'cancelled' || status === 'suspended') return status;
    
    if (currentPeriodEnd) {
      const endDate = currentPeriodEnd.toDate ? currentPeriodEnd.toDate() : new Date(currentPeriodEnd);
      const now = new Date();
      const daysPastDue = Math.floor((now - endDate) / (1000 * 60 * 60 * 24));
      
      if (daysPastDue < 0) return 'active';
      if (daysPastDue <= 7) return 'past_due';
      return 'suspended';
    }
    
    return status || 'unknown';
  };

  const handleUpdateSubscription = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const updates = {
        'subscription.status': subscriptionAction.status
      };

      // If extending subscription
      if (subscriptionAction.extendDays > 0) {
        const currentEnd = editingClub.subscription?.currentPeriodEnd?.toDate 
          ? editingClub.subscription.currentPeriodEnd.toDate() 
          : new Date();
        const newEnd = new Date(Math.max(currentEnd.getTime(), Date.now()));
        newEnd.setDate(newEnd.getDate() + subscriptionAction.extendDays);
        
        updates['subscription.currentPeriodEnd'] = Timestamp.fromDate(newEnd);
        updates['subscription.nextPaymentDue'] = Timestamp.fromDate(newEnd);
      }

      await updateDoc(doc(db, 'clubs', editingClub.id), updates);

      // Update local state
      setClubs(clubs.map(c => {
        if (c.id === editingClub.id) {
          return {
            ...c,
            subscription: {
              ...c.subscription,
              status: subscriptionAction.status,
              ...(subscriptionAction.extendDays > 0 && {
                currentPeriodEnd: updates['subscription.currentPeriodEnd'],
                nextPaymentDue: updates['subscription.nextPaymentDue']
              })
            }
          };
        }
        return c;
      }));

      setSuccess('Subscription updated successfully!');
      setTimeout(() => {
        setShowSubscriptionModal(false);
        setEditingClub(null);
      }, 1500);

    } catch (error) {
      console.error('Error updating subscription:', error);
      setError('Failed to update subscription.');
    }

    setSaving(false);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-TT', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="admin-dashboard super-admin">
      {/* Header */}
      <header className="admin-header super-admin-header">
        <div className="admin-header-left">
          <span className="admin-logo">🌐</span>
          <span className="admin-title">PLATFORM ADMIN</span>
        </div>
        <div className="admin-header-right">
          <span className="super-admin-badge">Super Admin</span>
          <span className="admin-profile-btn">{displayName}</span>
          <button onClick={handleLogout} className="admin-logout-btn">
            Logout
          </button>
        </div>
      </header>

      <div className="admin-layout">
        {/* Sidebar */}
        <aside className="admin-sidebar">
          <nav className="admin-nav">
            <a href="/super-admin" className="admin-nav-item active">
              <span className="nav-icon">🌐</span>
              Platform Overview
            </a>
            <a href="/super-admin/clubs" className="admin-nav-item">
              <span className="nav-icon">🏟️</span>
              Manage Clubs
            </a>
            <a href="/super-admin/admins" className="admin-nav-item">
              <span className="nav-icon">👤</span>
              Manage Admins
            </a>
            <div className="nav-divider"></div>
            <a href="/" className="admin-nav-item">
              <span className="nav-icon">🏠</span>
              ClubLink TT Home
            </a>
          </nav>
        </aside>

        {/* Main Content */}
        <main className="admin-main">
          <div className="admin-welcome">
            <h1>Platform Administration</h1>
            <p>Manage all clubs and platform-wide settings</p>
          </div>

          {/* Stats Grid */}
          <div className="admin-stats-grid">
            <div className="admin-stat-card">
              <div className="stat-icon">🏟️</div>
              <div className="stat-content">
                <span className="stat-number">{loading ? '-' : stats.totalClubs}</span>
                <span className="stat-label">Total Clubs</span>
              </div>
            </div>
            <div className="admin-stat-card">
              <div className="stat-icon">👥</div>
              <div className="stat-content">
                <span className="stat-number">{loading ? '-' : stats.totalUsers}</span>
                <span className="stat-label">Total Users</span>
              </div>
            </div>
            <div className="admin-stat-card">
              <div className="stat-icon">⚽</div>
              <div className="stat-content">
                <span className="stat-number">{loading ? '-' : stats.totalPlayers}</span>
                <span className="stat-label">Total Players</span>
              </div>
            </div>
            <div className="admin-stat-card">
              <div className="stat-icon">👕</div>
              <div className="stat-content">
                <span className="stat-number">{loading ? '-' : stats.totalTeams}</span>
                <span className="stat-label">Total Teams</span>
              </div>
            </div>
          </div>

          {/* Clubs Section */}
          <div className="admin-card">
            <div className="card-header">
              <h2>Registered Clubs</h2>
              <button className="btn-add" onClick={openAddModal}>
                + Add Club
              </button>
            </div>
            <div className="card-content">
              {loading ? (
                <p className="loading-text">Loading clubs...</p>
              ) : clubs.length === 0 ? (
                <div className="empty-state">
                  <span className="empty-icon">🏟️</span>
                  <h3>No Clubs Yet</h3>
                  <p>Create your first club to get started.</p>
                  <button className="btn-add" onClick={openAddModal}>
                    + Add Club
                  </button>
                </div>
              ) : (
                <div className="clubs-grid">
                  {clubs.map(club => {
                    const subStatus = getSubscriptionStatusForClub(club);
                    return (
                    <div key={club.id} className="club-card">
                      <div className="club-card-header" style={{ 
                        background: `linear-gradient(135deg, ${club.primaryColor || '#1e3a5f'}, ${club.secondaryColor || '#ce1126'})`
                      }}>
                        <h3>{club.name}</h3>
                        <span className="club-subdomain">{club.subdomain}.yourapp.com</span>
                      </div>
                      <div className="club-card-body">
                        <div className="club-info-row">
                          <span className="info-label">Email:</span>
                          <span className="info-value">{club.email || 'Not set'}</span>
                        </div>
                        <div className="club-info-row">
                          <span className="info-label">Phone:</span>
                          <span className="info-value">{club.phone || 'Not set'}</span>
                        </div>
                        <div className="club-info-row">
                          <span className="info-label">Created:</span>
                          <span className="info-value">{formatDate(club.createdAt)}</span>
                        </div>
                        <div className="club-info-row">
                          <span className="info-label">Subscription:</span>
                          <span className={`status-badge sub-${subStatus}`}>
                            {subStatus === 'active' && '✓ Active'}
                            {subStatus === 'past_due' && '⚠️ Past Due'}
                            {subStatus === 'suspended' && '⛔ Suspended'}
                            {subStatus === 'cancelled' && '✕ Cancelled'}
                            {subStatus === 'unknown' && '? Unknown'}
                          </span>
                        </div>
                        {club.subscription?.currentPeriodEnd && (
                          <div className="club-info-row">
                            <span className="info-label">Period End:</span>
                            <span className="info-value">{formatDate(club.subscription.currentPeriodEnd)}</span>
                          </div>
                        )}
                      </div>
                      <div className="club-card-actions">
                        <a 
                          href={`/?club=${club.subdomain}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="btn-view"
                        >
                          View Site
                        </a>
                        <button className="btn-edit" onClick={() => openEditModal(club)}>
                          Edit
                        </button>
                        <button className="btn-subscription" onClick={() => openSubscriptionModal(club)}>
                          Subscription
                        </button>
                        <button 
                          className="btn-delete"
                          onClick={() => handleDeleteClub(club.id, club.name)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  )})}
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="admin-card">
            <div className="card-header">
              <h2>Quick Actions</h2>
            </div>
            <div className="quick-actions">
              <button className="quick-action-btn" onClick={openAddModal}>
                <span>➕</span> Add New Club
              </button>
              <button className="quick-action-btn" onClick={() => {
                if (clubs.length > 0) {
                  navigate(`/admin/dashboard?club=${clubs[0].subdomain}`);
                } else {
                  setError('No clubs available. Add a club first.');
                }
              }}>
                <span>🏠</span> Go to Club Admin
              </button>
            </div>
          </div>
        </main>
      </div>

      {/* Add Club Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content modal-large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add New Club</h2>
              <button className="modal-close" onClick={() => setShowAddModal(false)}>×</button>
            </div>
            <form onSubmit={handleAddClub}>
              <div className="modal-body">
                {error && <div className="form-error">{error}</div>}
                {success && <div className="form-success">{success}</div>}

                <div className="form-group">
                  <label>Club Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={handleNameChange}
                    placeholder="e.g., ClubLink TT"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Subdomain *</label>
                  <div className="subdomain-input">
                    <input
                      type="text"
                      value={formData.subdomain}
                      onChange={e => setFormData({...formData, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '')})}
                      placeholder="proseriesfc"
                      required
                    />
                    <span className="subdomain-suffix">.yourapp.com</span>
                  </div>
                  <span className="form-hint">This will be the club's unique URL identifier</span>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Contact Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={e => setFormData({...formData, email: e.target.value})}
                      placeholder="info@club.com"
                    />
                  </div>
                  <div className="form-group">
                    <label>Contact Phone</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={e => setFormData({...formData, phone: e.target.value})}
                      placeholder="(868) 123-4567"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Address</label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={e => setFormData({...formData, address: e.target.value})}
                    placeholder="Club address"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Primary Color</label>
                    <div className="color-input">
                      <input
                        type="color"
                        value={formData.primaryColor}
                        onChange={e => setFormData({...formData, primaryColor: e.target.value})}
                      />
                      <span>{formData.primaryColor}</span>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Secondary Color</label>
                    <div className="color-input">
                      <input
                        type="color"
                        value={formData.secondaryColor}
                        onChange={e => setFormData({...formData, secondaryColor: e.target.value})}
                      />
                      <span>{formData.secondaryColor}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={() => setShowAddModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-save" disabled={saving}>
                  {saving ? 'Creating...' : 'Create Club'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Club Modal */}
      {showEditModal && editingClub && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content modal-large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Club</h2>
              <button className="modal-close" onClick={() => setShowEditModal(false)}>×</button>
            </div>
            <form onSubmit={handleUpdateClub}>
              <div className="modal-body">
                {error && <div className="form-error">{error}</div>}
                {success && <div className="form-success">{success}</div>}

                <div className="form-group">
                  <label>Club Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={e => setFormData({...formData, name: e.target.value})}
                    placeholder="e.g., ClubLink TT"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Subdomain</label>
                  <div className="subdomain-input">
                    <input
                      type="text"
                      value={formData.subdomain}
                      disabled
                      className="disabled"
                    />
                    <span className="subdomain-suffix">.yourapp.com</span>
                  </div>
                  <span className="form-hint">Subdomain cannot be changed after creation</span>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Contact Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={e => setFormData({...formData, email: e.target.value})}
                      placeholder="info@club.com"
                    />
                  </div>
                  <div className="form-group">
                    <label>Contact Phone</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={e => setFormData({...formData, phone: e.target.value})}
                      placeholder="(868) 123-4567"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Address</label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={e => setFormData({...formData, address: e.target.value})}
                    placeholder="Club address"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Primary Color</label>
                    <div className="color-input">
                      <input
                        type="color"
                        value={formData.primaryColor}
                        onChange={e => setFormData({...formData, primaryColor: e.target.value})}
                      />
                      <span>{formData.primaryColor}</span>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Secondary Color</label>
                    <div className="color-input">
                      <input
                        type="color"
                        value={formData.secondaryColor}
                        onChange={e => setFormData({...formData, secondaryColor: e.target.value})}
                      />
                      <span>{formData.secondaryColor}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-save" disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Subscription Management Modal */}
      {showSubscriptionModal && editingClub && (
        <div className="modal-overlay" onClick={() => setShowSubscriptionModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Manage Subscription</h2>
              <button className="modal-close" onClick={() => setShowSubscriptionModal(false)}>×</button>
            </div>
            <form onSubmit={handleUpdateSubscription}>
              <div className="modal-body">
                {error && <div className="form-error">{error}</div>}
                {success && <div className="form-success">{success}</div>}

                <div className="subscription-info-box">
                  <h3>{editingClub.name}</h3>
                  <p>Current Status: <strong>{getSubscriptionStatusForClub(editingClub)}</strong></p>
                  {editingClub.subscription?.currentPeriodEnd && (
                    <p>Period End: {formatDate(editingClub.subscription.currentPeriodEnd)}</p>
                  )}
                </div>

                <div className="form-group">
                  <label>Set Subscription Status</label>
                  <select
                    value={subscriptionAction.status}
                    onChange={e => setSubscriptionAction({...subscriptionAction, status: e.target.value})}
                  >
                    <option value="active">Active</option>
                    <option value="past_due">Past Due</option>
                    <option value="suspended">Suspended</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Extend Subscription (days)</label>
                  <input
                    type="number"
                    min="0"
                    max="365"
                    value={subscriptionAction.extendDays}
                    onChange={e => setSubscriptionAction({...subscriptionAction, extendDays: parseInt(e.target.value) || 0})}
                  />
                  <span className="form-hint">Add days to the current period end date (useful for offline payments)</span>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={() => setShowSubscriptionModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-save" disabled={saving}>
                  {saving ? 'Updating...' : 'Update Subscription'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default SuperAdminDashboard;
